import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { cn, formatMoney, formatPercent } from '@/lib/utils'

import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { quoteQueryOptions } from '../api/queries'
import { AiData } from '../ui/ai-data'
import { numberValue } from '../ui/numbers'
import { ErrorState, LoadingState, SurfaceCard } from '../ui/page'
import type { WorkflowTabProps } from '../workflow/types'
import {
  financingApi,
  financingQueryOptions,
  type Financing,
  type FinancingPreview,
  type FinancingState,
  type FinancingTerm,
  type FinancingTerms,
} from './api'

const day = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
const formatDay = (value: string) => day.format(new Date(`${value}T00:00:00Z`))

const termOptions: { value: FinancingTerm; label: string; detail: string }[] = [
  { value: 'half_on_shipment', label: '50% / 50%', detail: 'Deposit, then final shipment' },
  { value: 'upfront', label: '100% upfront', detail: 'Due when invoiced' },
  { value: 'net_30', label: 'Net 30', detail: '30 days after invoice issue' },
  { value: 'custom', label: 'Custom', detail: 'Agreed installment terms' },
  { value: 'prepaid', label: 'Prepaid', detail: 'Paid separately; no invoice' },
]

type FormValues = {
  term: FinancingTerm
  customTerms: string
  customPercent: string
  customDueDays: string
  prepaidReference: string
  prepaidNote: string
}
const fields = ['term', 'customTerms', 'customPercent', 'customDueDays', 'prepaidReference', 'prepaidNote'] as const

function savedValues(financing: Financing | null | undefined): FormValues {
  return {
    term: financing?.term_type ?? 'half_on_shipment',
    customTerms: financing?.custom_terms ?? '',
    customPercent: String(numberValue(financing?.invoice_percent ?? 50)),
    customDueDays: String(financing?.due_days ?? 0),
    prepaidReference: financing?.prepaid_reference ?? '',
    prepaidNote: financing?.prepaid_note ?? '',
  }
}

type CustomKey = 'customPercent' | 'customDueDays' | 'customTerms'
type PrepaidKey = 'prepaidReference' | 'prepaidNote'

// The saved custom and prepaid terms edit in place (USA-1580); the rules match `valid` below.
const customFields: InlineGroupField<CustomKey>[] = [
  { key: 'customPercent', label: 'First installment', required: true, inputMode: 'decimal', suffix: '%', className: 'col-span-3' },
  { key: 'customDueDays', label: 'Days after invoice issue', required: true, inputMode: 'numeric', suffix: 'days', className: 'col-span-3' },
  { key: 'customTerms', label: 'Agreed terms', required: true, kind: 'textarea' },
]
const prepaidFields: InlineGroupField<PrepaidKey>[] = [
  { key: 'prepaidReference', label: 'Payment reference', className: 'col-span-6 sm:col-span-2' },
  { key: 'prepaidNote', label: 'Note', kind: 'textarea', className: 'col-span-6 sm:col-span-4' },
]

function customProblems(draft: Record<CustomKey, string>): CustomKey[] {
  const percent = Number(draft.customPercent)
  const days = Number(draft.customDueDays)
  return [
    ...(draft.customPercent.trim() !== '' && !(percent > 0 && percent <= 100) ? (['customPercent'] as const) : []),
    ...(draft.customDueDays.trim() !== '' && !(Number.isInteger(days) && days >= 0 && days <= 365)
      ? (['customDueDays'] as const)
      : []),
    ...(draft.customTerms.length > 1000 ? (['customTerms'] as const) : []),
  ]
}

function prepaidProblems(draft: Record<PrepaidKey, string>): PrepaidKey[] {
  return [
    ...(draft.prepaidReference.length > 120 ? (['prepaidReference'] as const) : []),
    ...(draft.prepaidNote.length > 2000 ? (['prepaidNote'] as const) : []),
  ]
}

function TermDetail({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <span className={cn('block', wide && 'sm:col-span-2')}>
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className={cn('block whitespace-pre-wrap break-words', !value && 'text-muted-foreground')}>{value || 'Not set'}</span>
    </span>
  )
}

function termsFrom(values: FormValues): FinancingTerms {
  return {
    term_type: values.term,
    ...(values.term === 'custom'
      ? {
          custom_terms: values.customTerms.trim(),
          custom_invoice_percent: Number(values.customPercent),
          custom_due_days: Number(values.customDueDays),
        }
      : values.term === 'prepaid'
        ? {
            prepaid_reference: values.prepaidReference.trim() || null,
            prepaid_note: values.prepaidNote.trim() || null,
          }
        : {}),
  }
}

/** The planned invoices for the chosen terms (demo `InvoiceSchedule`). */
export function InvoiceSchedule({ plan }: { plan: FinancingPreview }) {
  return (
    <AiData asChild label="AI-assisted payment schedule" className="m-4">
      <section aria-label="Planned invoices" className="border-t">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3">
          <h3 className="text-sm font-semibold">Planned invoices</h3>
          <span className="text-sm text-muted-foreground">
            {plan.amount_source === 'issued_invoice' ? 'Invoiced quote' : 'Full quote'}
            <strong className="ml-1.5 font-semibold tabular-nums text-foreground">{formatMoney(plan.quote_amount)}</strong>
          </span>
        </div>
        {plan.installments.map((item) => {
          const trigger =
            item.trigger === 'prepaid'
              ? 'Paid separately'
              : item.trigger === 'final_shipment'
              ? 'At final shipment'
              : item.trigger === 'custom_terms'
                ? 'Per custom terms'
                : item.due_days
                  ? `${item.due_days} days after invoice issue`
                  : 'On invoice issue'
          const estimate = item.trigger !== 'prepaid' && item.estimated_due_start
            ? `${formatDay(item.estimated_due_start)}${
                item.estimated_due_end && item.estimated_due_end !== item.estimated_due_start
                  ? ` to ${formatDay(item.estimated_due_end)}`
                  : ''
              }`
            : null
          return (
            <details className="group border-t" key={item.number}>
              <summary className="grid cursor-pointer list-none grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 px-4 py-3 text-sm hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring sm:grid-cols-[minmax(100px,1fr)_minmax(180px,2fr)_120px_120px_16px] [&::-webkit-details-marker]:hidden">
                <span className="font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    Invoice {item.number}
                    <ChevronDown aria-hidden="true" className="size-3.5 text-muted-foreground transition-transform group-open:rotate-180 sm:hidden" />
                  </span>
                  <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                    {item.trigger === 'prepaid'
                      ? 'Paid'
                      : item.issued
                        ? 'Issued'
                        : item.number === 1 && plan.installments.length > 1
                          ? 'Deposit'
                          : 'Planned'}
                  </span>
                </span>
                <span className="col-start-1 row-start-2 sm:col-start-2 sm:row-start-1">
                  <span className="block">{trigger}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {item.trigger === 'prepaid'
                      ? 'No invoice'
                      : estimate
                        ? `Est. ${estimate}`
                        : item.issued
                          ? 'See issued invoice for due date'
                          : 'Timing not specified'}
                  </span>
                </span>
                <span className="col-start-2 row-start-1 text-right tabular-nums sm:col-start-3">
                  <strong className="font-semibold">{formatMoney(item.amount)}</strong>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{numberValue(item.percent)}%</span>
                </span>
                <span className="col-start-2 row-start-2 text-right tabular-nums sm:col-start-4 sm:row-start-1">
                  {formatMoney(item.remaining_balance)}
                  <span className="mt-0.5 block text-xs text-muted-foreground">Remaining</span>
                </span>
                <ChevronDown aria-hidden="true" className="hidden size-4 text-muted-foreground transition-transform group-open:rotate-180 sm:block" />
              </summary>
              <div className="border-t bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                {item.trigger === 'prepaid'
                  ? 'The customer already paid separately (check, wire, card on file). No invoice is sent; the project can start once the final quote is published.'
                  : item.trigger === 'invoice_issue'
                  ? item.issued
                    ? 'The issued invoice keeps its original amount and payment terms.'
                    : `Assumes invoice issue on ${formatDay(plan.estimate_as_of)}. The due date is set when the invoice is issued after final customer approval.`
                  : item.trigger === 'final_shipment'
                    ? estimate
                      ? `Estimated from all equipment being ready to ship, using lead times from ${formatDay(plan.shipment_anchor)}. Shipment is not confirmed; carrier transit is not added to payment timing.`
                      : 'Confirm equipment lead times in Shipping to estimate the final shipment. The balance is due at final shipment, not delivery.'
                    : 'The remaining balance follows the agreed custom terms. No balance due date is recorded.'}
              </div>
            </details>
          )
        })}
      </section>
    </AiData>
  )
}

/** The Payment stage (stage key `financing`), ported from the demo's `FinancingTab`. */
export function PaymentTab({ active = true, quoteId, quote, onQuoteChange, onWorkflowChange }: WorkflowTabProps) {
  const api = useQuoteBuilderApi()
  const queryClient = useQueryClient()
  const latestRevision = useRef(quote.revision)
  latestRevision.current = Math.max(latestRevision.current, quote.revision)
  const [acknowledged, setAcknowledged] = useState<{ revision: number; values: FormValues } | null>(null)
  const [refreshNeeded, setRefreshNeeded] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const refreshSequence = useRef(0)
  const query = useQuery({ ...financingQueryOptions(api, quoteId), enabled: active })
  const lastState = useRef<FinancingState | undefined>(undefined)
  if (query.data && query.data.quote_revision >= (lastState.current?.quote_revision ?? 0)) lastState.current = query.data
  const state = lastState.current
  const needsRefresh = refreshNeeded || query.isError
  const financing = state?.financing
  const baseline =
    acknowledged && acknowledged.revision >= (state?.quote_revision ?? 0) ? acknowledged.values : savedValues(financing)
  const [edits, setEdits] = useState<Partial<FormValues>>({})
  const [editingTerms, setEditingTerms] = useState(false)
  const locked = Boolean(state?.terms_locked)
  const values = locked ? savedValues(financing) : { ...baseline, ...edits }
  const { term, customTerms, customPercent, customDueDays, prepaidReference, prepaidNote } = values
  const dirty = fields.some((field) => edits[field] !== undefined && edits[field] !== baseline[field])
  const terms = termsFrom(values)
  const valid =
    (term === 'prepaid' && prepaidReference.length <= 120 && prepaidNote.length <= 2000) ||
    (term !== 'custom' && term !== 'prepaid') ||
    (customTerms.trim().length > 0 &&
      customTerms.length <= 1000 &&
      customPercent !== '' &&
      Number(customPercent) > 0 &&
      Number(customPercent) <= 100 &&
      customDueDays !== '' &&
      Number.isInteger(Number(customDueDays)) &&
      Number(customDueDays) >= 0 &&
      Number(customDueDays) <= 365)
  const preview = useQuery({
    queryKey: ['quote-financing-preview', quoteId, quote.revision, terms],
    queryFn: ({ signal }) => financingApi(api).preview(quoteId, terms, signal),
    enabled: active && valid && !locked && Boolean(state) && dirty,
    retry: false,
    placeholderData: keepPreviousData,
  })
  const plan = dirty && !locked ? (preview.data ?? state?.preview) : state?.preview

  async function refresh() {
    const sequence = ++refreshSequence.current
    setRefreshing(true)
    const financingOptions = financingQueryOptions(api, quoteId)
    const [quoteResult, financingResult] = await Promise.allSettled([
      queryClient.fetchQuery({ ...quoteQueryOptions(api, quoteId), staleTime: 0 }),
      queryClient.fetchQuery({ ...financingOptions, staleTime: 0 }),
    ])
    if (quoteResult.status === 'fulfilled' && quoteResult.value.revision >= latestRevision.current) {
      latestRevision.current = quoteResult.value.revision
      onQuoteChange(quoteResult.value)
    }
    if (financingResult.status === 'fulfilled' && financingResult.value.quote_revision >= latestRevision.current) {
      queryClient.setQueryData(financingOptions.queryKey, financingResult.value)
    }
    const recovered =
      quoteResult.status === 'fulfilled' &&
      financingResult.status === 'fulfilled' &&
      quoteResult.value.revision >= latestRevision.current &&
      financingResult.value.quote_revision >= latestRevision.current
    if (sequence === refreshSequence.current) {
      setRefreshNeeded(!recovered)
      setRefreshing(false)
    }
    return recovered
  }

  const mutation = useMutation({
    mutationFn: (submitted: FormValues) => financingApi(api).setTerms(quoteId, latestRevision.current, termsFrom(submitted)),
    onMutate: () => {
      ++refreshSequence.current
      setRefreshing(false)
      setRefreshNeeded(false)
      return queryClient.cancelQueries({ queryKey: financingQueryOptions(api, quoteId).queryKey })
    },
    onSuccess: (workflow, submitted) => {
      if (workflow.revision >= latestRevision.current) {
        latestRevision.current = workflow.revision
        onWorkflowChange(workflow)
        setAcknowledged({ revision: workflow.revision, values: submitted })
      }
      // Acknowledged writes stay saved even when the subsequent reads fail.
      setEdits(
        (current) =>
          Object.fromEntries(
            Object.entries(current).filter(([field, value]) => value !== submitted[field as keyof FormValues]),
          ) as Partial<FormValues>,
      )
      void refresh()
    },
    onError: async (error) => {
      if (error instanceof QuoteBuilderApiError && (error.status === 412 || error.code === 'invoice_already_created')) {
        await refresh()
      }
    },
  })

  useEffect(() => {
    if (active && !mutation.isPending && state && quote.revision > state.quote_revision) {
      void queryClient.invalidateQueries({ queryKey: financingQueryOptions(api, quoteId).queryKey }, { cancelRefetch: false })
    }
  }, [active, api, quote.revision, mutation.isPending, query.dataUpdatedAt, queryClient, quoteId, state])

  const serializedValues = JSON.stringify(values)
  useEffect(() => {
    if (!dirty || !valid || locked || mutation.isPending || mutation.isError) return
    const timer = window.setTimeout(() => mutation.mutate(JSON.parse(serializedValues) as FormValues), 400)
    return () => window.clearTimeout(timer)
    // The demo autosaves 400ms after the last edit; `mutation` is stable per render cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedValues, dirty, valid, locked, mutation.isPending, mutation.isError])

  function edit(changes: Partial<FormValues>) {
    setEdits((current) => {
      const next = { ...current, ...changes }
      for (const field of Object.keys(changes) as (keyof FormValues)[]) {
        if (next[field] === baseline[field] && !mutation.isPending) delete next[field]
      }
      return next
    })
  }

  const save = () => {
    if (valid && !locked && !mutation.isPending) mutation.mutate(values)
  }

  if (query.isLoading) return <LoadingState label="Loading payment terms" />
  if (query.isError && !state) {
    return <ErrorState title="Payment terms unavailable" description="Reload the quote and try again." onRetry={() => void query.refetch()} />
  }
  const conflict = mutation.error instanceof QuoteBuilderApiError && mutation.error.status === 412
  const saved = Boolean(financing || acknowledged)

  // The custom and prepaid details edit in their own in-place forms, so this is not a form itself.
  return (
    <SurfaceCard title="Payment terms">
      <div>
        <fieldset className="grid gap-2 p-4 sm:grid-cols-2 lg:grid-cols-5" disabled={locked}>
          <legend className="sr-only">Payment terms</legend>
          {termOptions.map((option) => (
            <label
              className={cn(
                'flex min-h-16 items-start gap-3 rounded-lg border p-3 transition-colors',
                locked ? 'cursor-default' : 'cursor-pointer',
                term === option.value ? 'border-foreground bg-muted/50' : 'hover:bg-muted/40',
              )}
              key={option.value}
            >
              <input
                checked={term === option.value}
                className="mt-0.5 size-4 accent-foreground"
                name="financing-term"
                onChange={() => {
                  edit({ term: option.value })
                  // Custom terms with nothing agreed yet open for typing, as the old boxes did.
                  setEditingTerms(option.value === 'custom' && !customTerms.trim())
                }}
                type="radio"
              />
              <span>
                <span className="block text-sm font-semibold">{option.label}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{option.detail}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {term === 'prepaid' ? (
          <div className="border-t bg-muted/30 p-4">
            <InlineFieldGroup
              label="Prepaid payment"
              fields={prepaidFields}
              values={{ prepaidReference, prepaidNote }}
              invalid={prepaidProblems}
              notice={(draft) =>
                prepaidProblems(draft).length
                  ? 'Keep the payment reference to 120 characters and the note to 2,000.'
                  : null
              }
              labelled
              disabled={locked}
              editing={editingTerms && !locked}
              onEditingChange={setEditingTerms}
              onSave={(next) => {
                edit(next)
                setEditingTerms(false)
              }}
            >
              <span className="grid gap-3 text-sm sm:grid-cols-[minmax(0,16rem)_1fr]">
                <TermDetail label="Payment reference" value={prepaidReference} />
                <TermDetail label="Note" value={prepaidNote} />
              </span>
            </InlineFieldGroup>
          </div>
        ) : null}
        {term === 'custom' ? (
          <div className="border-t bg-muted/30 p-4">
            <InlineFieldGroup
              label="Custom terms"
              fields={customFields}
              values={{ customPercent, customDueDays, customTerms }}
              invalid={customProblems}
              notice={(draft) => {
                const problems = customProblems(draft)
                const messages: ReactNode[] = [
                  problems.includes('customPercent') && 'The first installment must be above 0% and at most 100%.',
                  problems.includes('customDueDays') && 'Days after invoice issue must be a whole number from 0 to 365.',
                  problems.includes('customTerms') && 'Keep the agreed terms to 1,000 characters.',
                ].filter(Boolean)
                return messages.length ? messages.map((message, index) => <p key={index}>{message}</p>) : null
              }}
              labelled
              disabled={locked}
              editing={editingTerms && !locked}
              onEditingChange={setEditingTerms}
              onSave={(next) => {
                edit(next)
                setEditingTerms(false)
              }}
            >
              <span className="grid gap-3 text-sm sm:grid-cols-2">
                <TermDetail label="First installment" value={customPercent.trim() ? formatPercent(customPercent) : ''} />
                <TermDetail
                  label="Due"
                  value={customDueDays.trim() ? `${customDueDays} days after invoice issue` : ''}
                />
                <TermDetail label="Agreed terms" value={customTerms} wide />
              </span>
            </InlineFieldGroup>
          </div>
        ) : null}
        {!valid ? (
          <p className="border-t px-4 py-3 text-sm text-muted-foreground" role="status">
            Enter the agreed terms, a first installment above 0%, and 0 to 365 days after invoice issue.
          </p>
        ) : (
          <>
            {dirty && preview.isError && !locked ? (
              <p className="border-t px-4 py-3 text-sm text-destructive" role="alert">
                Schedule unavailable. Check that the first installment is at least $0.01, then try again.
              </p>
            ) : (dirty && (preview.isFetching || preview.isPlaceholderData)) || (plan && plan.quote_revision < latestRevision.current) ? (
              <p className="border-t px-4 py-2 text-sm text-muted-foreground" role="status">Previous schedule · Updating...</p>
            ) : null}
            {plan ? (
              <InvoiceSchedule plan={plan} />
            ) : query.isFetching || preview.isFetching ? (
              <p className="border-t px-4 py-3 text-sm text-muted-foreground" role="status">Updating schedule...</p>
            ) : (
              <p className="border-t px-4 py-3 text-sm text-muted-foreground">Add a positive quote amount to preview invoices.</p>
            )}
          </>
        )}
        <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
          <span className="text-sm text-muted-foreground">
            {locked ? 'Terms locked after invoice creation' : dirty ? 'Unsaved terms' : saved ? 'Saved terms' : 'Default 50% / 50%'}
          </span>
          {!locked ? (
            <LoadingButton
              disabled={!valid || (saved && !dirty)}
              loading={mutation.isPending}
              loadingText="Saving..."
              onClick={save}
              size="sm"
              type="button"
            >
              {mutation.isError ? 'Retry save' : saved && !dirty ? 'Terms saved' : 'Save terms'}
            </LoadingButton>
          ) : (
            <Link
              className="text-sm font-medium text-primary hover:underline"
              params={{ quoteId, stage: 'invoicing' }}
              to="/quotes/$quoteId/workflow/$stage"
            >
              View invoice
            </Link>
          )}
        </div>
        {needsRefresh ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-sm" role="status">
            <span>{mutation.isSuccess ? 'Terms saved. Refresh to load the latest schedule.' : 'Your edits are kept. Refresh to load the latest quote.'}</span>
            <Button disabled={refreshing} onClick={() => void refresh()} size="sm" type="button" variant="outline">
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        ) : null}
        {mutation.isError ? (
          <p className="border-t border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive" role="alert">
            {conflict
              ? 'The quote changed. Your edits are kept; review the latest terms before saving again.'
              : 'Payment terms could not be saved. Refresh the quote and try again.'}
          </p>
        ) : null}
      </div>
    </SurfaceCard>
  )
}
